import { useEffect, useState } from "react";

import { usersService } from "@/modules/dashboard/users/services/users.service";

import { PaymentsPayoutService, PaymentsStatusChangesService } from "../services/payoutRescue.service";
import type { PaymentStatusChange } from "../services/payoutRescue.service";
import type { RescuePayout } from "../types/payoutRescue.types";

/**
 * Session cache: actor_user_id → display label (name or email). Status-change actors repeat constantly (the
 * same ops team) — one `GET /internal/users/:id` per distinct actor per session is enough. A failed
 * resolution caches `null` so we never hammer the endpoint for a deleted user.
 */
const actorLabelCache = new Map<string, string | null>();

async function resolveActor(id: string): Promise<string | null> {
  if (actorLabelCache.has(id)) return actorLabelCache.get(id) ?? null;
  try {
    const user = await usersService.getById(id);
    const label = user.full_name?.trim() || user.email || null;
    actorLabelCache.set(id, label);
    return label;
  } catch {
    actorLabelCache.set(id, null);
    return null;
  }
}

export interface UsePayoutRescueDetailResult {
  /** Live copy of the row — starts as the list row, refreshed ONCE by `GET /:id` on open (no polling). */
  payout: RescuePayout;
  /** Real transition log (`seq ASC`), or null while loading / on error (the drawer shows a fallback). */
  timeline: PaymentStatusChange[] | null;
  isTimelineLoading: boolean;
  hasTimelineError: boolean;
  /** Actor_user_id → resolved display label (missing key = unresolved; render the short id). */
  actorLabels: Record<string, string>;
}

/**
 * Drawer state for one payout. Freshness model: ONE `GET /:id` when the drawer opens (also future-proofs the
 * destination snapshot — it lights up the moment the backend serializes it) and a timeline fetch that re-runs
 * when the status flips via an action response. Deliberately NO interval polling (design brief §5) — a payout
 * left PROCESSING settles server-side and shows on the next manual refresh.
 */
export const usePayoutRescueDetail = (initialPayout: RescuePayout): UsePayoutRescueDetailResult => {
  const [payout, setPayout] = useState<RescuePayout>(initialPayout);
  const [timeline, setTimeline] = useState<PaymentStatusChange[] | null>(null);
  const [isTimelineLoading, setIsTimelineLoading] = useState(true);
  const [hasTimelineError, setHasTimelineError] = useState(false);
  const [actorLabels, setActorLabels] = useState<Record<string, string>>({});

  // A different payout was opened, or the list row was reconciled from an action response.
  useEffect(() => {
    setPayout(initialPayout);
  }, [initialPayout]);

  // Single freshness fetch on open.
  useEffect(() => {
    let cancelled = false;
    PaymentsPayoutService.getPayout(initialPayout.id)
      .then((res) => {
        if (!cancelled) setPayout(res.data as RescuePayout);
      })
      .catch(() => {
        // The list row is already rendered — a failed detail refresh silently keeps it.
      });
    return () => {
      cancelled = true;
    };
  }, [initialPayout.id]);

  // Audit timeline — refetches when the status flips so the new transition appears after a rescue.
  useEffect(() => {
    let cancelled = false;
    setIsTimelineLoading(true);
    setHasTimelineError(false);
    PaymentsStatusChangesService.listByOperation("PAYOUT", initialPayout.id)
      .then(async (res) => {
        if (cancelled) return;
        setTimeline(res.data);
        // Resolve the distinct staff actors to names (cached per session).
        const ids = [...new Set(res.data.map((c) => c.actor_user_id).filter((id): id is string => !!id))];
        const entries = await Promise.all(ids.map(async (id) => [id, await resolveActor(id)] as const));
        if (cancelled) return;
        setActorLabels((prev) => {
          const next = { ...prev };
          for (const [id, label] of entries) {
            if (label) next[id] = label;
          }
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setTimeline(null);
          setHasTimelineError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsTimelineLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialPayout.id, payout.status]);

  return { payout, timeline, isTimelineLoading, hasTimelineError, actorLabels };
};
